import axios from 'axios';
import { STORAGE_KEYS } from '@utils/storageKeys';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Decodifica o payload de um JWT sem validar assinatura.
 * Retorna null se o token é inválido.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

/** Controle para evitar refresh simultâneo */
let refreshPromise = null;

/**
 * Faz refresh do token se ele está a menos de 5 minutos de expirar.
 * Retorna o token (novo ou existente).
 */
async function ensureFreshToken() {
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return token;

  const nowSec = Math.floor(Date.now() / 1000);
  const timeLeft = payload.exp - nowSec;

  // Se faltam mais de 5 minutos, o token ainda é válido
  if (timeLeft > 300) return token;

  // Se já expirou, não tentar refresh (vai dar 401 e redirecionar)
  if (timeLeft <= 0) return token;

  // Refresh proativo — evitar chamadas simultâneas
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${api.defaults.baseURL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const newToken = res.data?.data?.token;
        if (newToken) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
          return newToken;
        }
        return token;
      })
      .catch(() => token) // Em caso de erro, seguir com o token atual
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// Interceptor de request — adicionar token (com refresh proativo)
api.interceptors.request.use(
  async (config) => {
    // Não tentar refresh em chamadas de auth (login, register, etc.)
    const isAuthEndpoint = config.url?.startsWith('/auth/');

    let token;
    if (isAuthEndpoint) {
      token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    } else {
      token = await ensureFreshToken();
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response — tratar erros
api.interceptors.response.use(
  (response) => {
    return response.data?.data || response.data;
  },
  (error) => {
    const { response } = error;

    if (response?.status === 401) {
      const isLoginPage = window.location.pathname === '/login';
      const isRegisterPage = window.location.pathname === '/register';

      if (!isLoginPage && !isRegisterPage) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.TENANT);
        window.location.href = '/login';
      }
    }

    if (response?.status === 403) {
      const message = response.data?.error?.message;
      if (message?.includes('limit') || message?.includes('plan')) {
        console.warn('Limite do plano atingido:', message);
      }
    }

    if (response?.status >= 500) {
      console.error('Erro do servidor:', response.data);
    }

    return Promise.reject(error);
  }
);

export default api;
